import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function CapabilitiesPage() {
  const capabilities = [
    {
      icon: 'ri-brain-line',
      title: 'Machine Learning Models',
      description: 'Custom-built ML models that learn from your data, delivering predictive insights and intelligent pattern recognition for complex business challenges.',
      features: [
        'Supervised & Unsupervised Learning',
        'Deep Neural Networks',
        'Natural Language Processing',
        'Computer Vision Systems',
        'Reinforcement Learning'
      ]
    },
    {
      icon: 'ri-robot-2-line',
      title: 'Intelligent Automation',
      description: 'Automate repetitive processes with AI-driven workflows that adapt and optimize over time, reducing costs and increasing operational efficiency.',
      features: [
        'Robotic Process Automation',
        'Workflow Optimization',
        'Smart Document Processing',
        'Intelligent Task Routing',
        'Adaptive Process Learning'
      ]
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Predictive Analytics',
      description: 'Transform historical data into future insights with advanced forecasting models that help you make proactive, data-driven decisions.',
      features: [
        'Time Series Forecasting',
        'Demand Prediction',
        'Risk Assessment Models',
        'Trend Analysis',
        'Anomaly Detection'
      ]
    },
    {
      icon: 'ri-database-2-line',
      title: 'Data Intelligence',
      description: 'Extract meaningful insights from vast datasets using sophisticated AI algorithms that uncover hidden patterns and opportunities.',
      features: [
        'Big Data Processing',
        'Pattern Recognition',
        'Data Mining & Analysis',
        'Real-time Analytics',
        'Insight Generation'
      ]
    },
    {
      icon: 'ri-code-box-line',
      title: 'Custom AI Development',
      description: 'Bespoke AI solutions tailored to your unique requirements, from concept to deployment, ensuring perfect alignment with your business goals.',
      features: [
        'End-to-End Development',
        'Model Training & Tuning',
        'API Development',
        'Cloud Deployment',
        'Continuous Optimization'
      ]
    },
    {
      icon: 'ri-plug-line',
      title: 'AI Integration',
      description: 'Seamlessly integrate AI capabilities into your existing systems and workflows with minimal disruption and maximum impact.',
      features: [
        'Legacy System Integration',
        'API & Microservices',
        'Cloud Platform Support',
        'Real-time Data Pipelines',
        'Scalable Architecture'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Inter',sans-serif]">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-red-50">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-orange-400/20 to-transparent rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="text-orange-700 text-xs uppercase tracking-wider font-semibold mb-4">
              Our Capabilities
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              AI-Powered Solutions<br />Across Every Layer
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              From machine learning models to intelligent automation systems, FluxByte delivers 
              scalable AI that adapts to your business needs and drives measurable results.
            </p>
          </div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <div 
                key={index}
                className="bg-white border-l-2 border-orange-500 p-8 rounded-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="w-14 h-14 flex items-center justify-center mb-6">
                  <i className={`${capability.icon} text-4xl bg-gradient-to-br from-orange-600 to-red-600 bg-clip-text text-transparent`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{capability.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">{capability.description}</p>
                
                <div className="space-y-2">
                  {capability.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <i className="ri-check-line text-orange-600 text-sm mt-0.5"></i>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Development Process
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              A proven methodology that ensures successful AI implementation from concept to deployment
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery & Analysis',
                description: 'We analyze your business challenges, data landscape, and objectives to design the optimal AI solution.'
              },
              {
                step: '02',
                title: 'Model Development',
                description: 'Our team builds and trains custom AI models using cutting-edge algorithms and your specific data.'
              },
              {
                step: '03',
                title: 'Integration & Testing',
                description: 'Seamless integration with your existing systems, followed by rigorous testing and validation.'
              },
              {
                step: '04',
                title: 'Deployment & Support',
                description: 'Full deployment with ongoing monitoring, optimization, and support to ensure continued success.'
              }
            ].map((process, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-extrabold text-orange-100 mb-4">{process.step}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-red-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Let's Build Your AI Solution
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Ready to leverage AI for your business? Contact our team to discuss your project.
          </p>
          <Link 
            to="/contact"
            className="inline-block px-8 py-4 bg-white text-orange-600 rounded-full font-semibold text-base hover:shadow-2xl transition-all cursor-pointer whitespace-nowrap"
          >
            Get Started →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
